import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ToastService } from './../services/toast.service';
import { UserInfoDTO, FilterOperator, ClientDTO, FilterCondition, UserAccountClient, Role } from './../models/models.swagger.generated';
import { tap, catchError } from 'rxjs/operators';
import { IAdaptorService } from './../shared/syncfusion/adaptor-service.interface';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { FilterDTO, PagedResultDTOOfUserInfoDTO, SortDirection } from '../models/models.swagger.generated';
import { ShowInactiveService } from '../shared/grid-base/show-inactive.service';
import { ErrorType } from '../../core/routing/error-types';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService extends ShowInactiveService implements IAdaptorService {
  public selectedClient: ClientDTO;

  constructor(
    private accountClient: UserAccountClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService
  ) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfUserInfoDTO> {
    if (!this.showInactive) {
      if (!filters) {
        filters = [];
      }
      filters.push(new FilterDTO({isComplex: false, field: 'Enabled', operator: FilterOperator.Equal, value: 'true'}));
      // exclude REST API users from user management
      filters.push(new FilterDTO({isComplex: false, field: 'UserName', operator: FilterOperator.DoesNotStartWith, value: 'ApiKeyUser'}));
    }
    return this.accountClient.searchUsers(this.selectedClient?.id, skip, take, sortBy, sortDirection, filters).pipe(
      tap((result: any) => {
        if (result.items) {
          result.items.forEach((item) => {
            if (!item.client) {
              item.client = {name: ''} as any;
            }
          });
        }
      }),
    );
  }

  public updateRecord(data: any): Observable<UserInfoDTO> {
    if (data.Roles.includes(Role.AssetStorageGroupMember)){
      return this.accountClient.updateAssetStorageGroupMemberUser(data).pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-edit.success-title',
            'i18n.core.messages.user-edit.success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.core.messages.user-edit.title', 'i18n.core.messages.user-edit.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
    } else if (data.Roles.includes(Role.InterfaceServiceAccess)) {
      return this.accountClient.updateInterfaceUser(data).pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-edit.success-title',
            'i18n.core.messages.user-edit.success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.core.messages.user-edit.title', 'i18n.core.messages.user-edit.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
    } else {
      return this.accountClient.updateUser(data).pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-edit.success-title',
            'i18n.core.messages.user-edit.success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.core.messages.user-edit.title', 'i18n.core.messages.user-edit.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
    }
  }

  public addRecord(data: any): Observable<UserInfoDTO> {
    if (data.Roles.includes(Role.AssetStorageGroupMember)){
      return this.accountClient.addAssetStorageGroupMemberUser(data).pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-add.success-title',
            'i18n.core.messages.user-add.success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.core.messages.user-add.title', 'i18n.core.messages.user-add.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
    } else {
      return this.accountClient.addInterfaceUser(data).pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-add.success-title',
            'i18n.core.messages.user-add.success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.core.messages.user-add.title', 'i18n.core.messages.user-add.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
    }
  }

  public deleteRecord(id: number): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public deleteServiceUser(id: string): Observable<void> {
    return this.accountClient.deleteServiceUser(id).pipe(
      tap(() => {
        this.toastService.showSuccess('i18n.base.user-management.messages.delete-service-user-success.title', 'i18n.base.user-management.messages.delete-service-user-success.message');
      }),
      catchError((error: any) => {
        this.toastService.showError('i18n.base.user-management.messages.delete-failed.title', 'i18n.base.user-management.messages.delete-failed.message', null, ErrorType.serverError, error.response);
        return throwError(() => error);
      }),
    );
  }

  public deleteAssetStorageGroupMember(id: string): Observable<void> {
    return this.accountClient.deleteAssetStorageGroupMember(id).pipe(
      tap(() => {
        this.toastService.showSuccess('i18n.base.user-management.messages.delete-asset-storage-group-member-success.title', 'i18n.base.user-management.messages.delete-asset-storage-group-member-success.message');
      }),
      catchError((error: any) => {
        this.toastService.showError('i18n.base.user-management.messages.delete-failed.title', 'i18n.base.user-management.messages.delete-failed.message', null, ErrorType.serverError, error.response);
        return throwError(() => error);
      }),
    );
  }

  public searchUsers(searchTerm: string, take: number, filters: FilterDTO[]): Observable<PagedResultDTOOfUserInfoDTO> {
    if (!filters) {
      filters = [];
    }
    filters.push(new FilterDTO({isComplex: false, field: 'IsActive', operator: FilterOperator.Equal, value: 'true'}));
    searchTerm = searchTerm?.trim();
    if(searchTerm != null && searchTerm.length) {
      filters.push( new FilterDTO({
        isComplex: true,
        condition: FilterCondition.Or,
        predicates: [
          new FilterDTO({isComplex: false, field: 'UserName', operator: FilterOperator.Contains, value: searchTerm}),
          new FilterDTO({isComplex: false, field: 'Firstname', operator: FilterOperator.Contains, value: searchTerm}),
          new FilterDTO({isComplex: false, field: 'Lastname', operator: FilterOperator.Contains, value: searchTerm}),
          new FilterDTO({isComplex: false, field: 'Email', operator: FilterOperator.Contains, value: searchTerm})
        ]
      }));
    }

    return this.getData(0, take, filters, 'UserName', SortDirection.Ascending);
  }

  public reset2FARegistration(userId: string): Observable<void> {
    return this.accountClient.reset2FARegistration(userId).pipe(
      tap(() => {
        this.toastService.showSuccess('i18n.base.user-management.messages.reset-2fa-success.title', 'i18n.base.user-management.messages.reset-2fa-success.message');
      }),
      catchError((error: any) => {
        this.toastService.showError('i18n.base.user-management.messages.reset-2fa-failed.title', 'i18n.base.user-management.messages.reset-2fa-failed.message', null, ErrorType.serverError, error.response);
        return throwError(() => error);
      }),
    );
  }

  public resendOnboardingEmail(userId: string): Observable<void> {
    this.spinnerService.show();
    return this.accountClient.resendUserOnboardingEmail(userId).pipe(
      tap(() => {
        this.spinnerService.hide();
        this.toastService.showSuccess('i18n.base.user-management.messages.resend-onboarding-email.title', 'i18n.base.user-management.messages.resend-onboarding-email.success');
      }),
      catchError((error: any) => {
        this.spinnerService.hide();
        this.toastService.showError('i18n.base.user-management.messages.resend-onboarding-email.title', 'i18n.base.user-management.messages.resend-onboarding-email.failed', null, ErrorType.serverError, error.response);
        return throwError(() => error);
      }),
    );
  }
}
